import { FilterChip, FilterGroup } from '@src/components/shared/FilterChip';
import { PageHeader } from '@src/components/shared/PageHeader';
import { Badge } from '@src/components/ui/badge';
import { Button } from '@src/components/ui/button';
import { Card } from '@src/components/ui/card';
import { Input } from '@src/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@src/components/ui/table';
import { errorMessage } from '@src/lib/errors';
import { formatRunDate, formatUtcTooltip } from '@src/lib/runFormat';
import { resources } from '@src/resources';
import {
  type CircuitBreakerTripsQueryParams,
  fetchCircuitBreakerTrips,
  type TripStatus,
} from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';
import { type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

/** The preset window chips, plus a frontend-only 'custom' mode -- when selected, `since`/`until`
 * are sent directly instead of `window` (see queryParams below). Mirrors Runs.tsx's own
 * WindowSelection, minus 'today' -- a trip is rare enough that "today" is rarely a useful cut, and
 * every other list screen in this dashboard defaults to a broader window anyway. */
type WindowSelection = 'today' | '7d' | '30d' | 'all' | 'custom';

const DEFAULT_WINDOW: WindowSelection = '7d';

const WINDOW_OPTIONS: { value: WindowSelection; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const STATUS_OPTIONS: { value?: TripStatus; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'active', label: resources.circuitBreakerTrips.statusActive },
  { value: 'expired', label: resources.circuitBreakerTrips.statusExpired },
];

/** Exported so Overview.tsx's summary card renders the exact same badge color/label per status --
 * the two views must never disagree on what "active" looks like. */
export function statusBadgeVariant(status: TripStatus): 'critical' | 'secondary' {
  return status === 'active' ? 'critical' : 'secondary';
}

export function statusLabel(status: TripStatus): string {
  return status === 'active' ? resources.circuitBreakerTrips.statusActive : resources.circuitBreakerTrips.statusExpired;
}

export function CircuitBreakers() {
  // Every filter lives in the URL (not useState) so the current view is shareable/bookmarkable
  // and survives a refresh or back/forward navigation -- same convention as Runs.tsx.
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = (key: string, value: string | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  const dateWindow = (searchParams.get('window') as WindowSelection | null) ?? DEFAULT_WINDOW;
  const status = (searchParams.get('status') as TripStatus | null) ?? undefined;
  const circuitBreakerKey = searchParams.get('key') ?? '';
  const customFrom = searchParams.get('from') ?? '';
  const customTo = searchParams.get('to') ?? '';

  const setDateWindow = (value: WindowSelection) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_WINDOW) {
          next.delete('window');
        } else {
          next.set('window', value);
        }
        if (value !== 'custom') {
          next.delete('from');
          next.delete('to');
        }
        return next;
      },
      { replace: true },
    );
  };
  const setStatus = (value: TripStatus | undefined) => setParam('status', value);
  const setCircuitBreakerKey = (value: string) => setParam('key', value);
  const setCustomFrom = (value: string) => setParam('from', value);
  const setCustomTo = (value: string) => setParam('to', value);

  const trimmedKey = circuitBreakerKey.trim();
  const isCustomWindow = dateWindow === 'custom';
  // datetime-local gives a value with no timezone offset (interpreted as local time by `new Date()`,
  // which is what we want -- the picker itself is already showing the user's local time).
  const customSince = isCustomWindow && customFrom ? new Date(customFrom).toISOString() : undefined;
  const customUntil = isCustomWindow && customTo ? new Date(customTo).toISOString() : undefined;

  const queryParams: CircuitBreakerTripsQueryParams = {
    window: isCustomWindow ? undefined : (dateWindow as CircuitBreakerTripsQueryParams['window']),
    since: customSince,
    until: customUntil,
    status,
    circuitBreakerKey: trimmedKey || undefined,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops-circuit-breaker-trips', queryParams],
    queryFn: () => fetchCircuitBreakerTrips(queryParams),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const hasActiveFilters = dateWindow !== DEFAULT_WINDOW || status !== undefined || trimmedKey !== '';

  const clearFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  const resultsLabel = isLoading
    ? resources.common.loading
    : total > items.length
      ? `Showing ${items.length} of ${total.toLocaleString()} trips`
      : `${total.toLocaleString()} trip${total === 1 ? '' : 's'} matched`;

  return (
    <div>
      <PageHeader title={resources.pages.circuitBreakers.title} subtitle={resources.pages.circuitBreakers.subtitle} />

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-6">
          <FilterGroup label={resources.circuitBreakerTrips.windowLabel}>
            {WINDOW_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                active={dateWindow === opt.value}
                onClick={() => setDateWindow(opt.value)}
              />
            ))}
          </FilterGroup>

          {isCustomWindow && (
            <FilterGroup label={resources.circuitBreakerTrips.customRangeLabel}>
              <Input
                type="datetime-local"
                value={customFrom}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomFrom(event.target.value)}
                className="w-[190px]"
                aria-label={resources.circuitBreakerTrips.customFromLabel}
              />
              <span className="text-xs text-muted-foreground">{resources.circuitBreakerTrips.customRangeSeparator}</span>
              <Input
                type="datetime-local"
                value={customTo}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomTo(event.target.value)}
                className="w-[190px]"
                aria-label={resources.circuitBreakerTrips.customToLabel}
              />
            </FilterGroup>
          )}

          <FilterGroup label={resources.circuitBreakerTrips.statusLabel}>
            {STATUS_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.label}
                label={opt.label}
                active={status === opt.value}
                onClick={() => setStatus(opt.value)}
              />
            ))}
          </FilterGroup>

          <FilterGroup label={resources.circuitBreakerTrips.keyLabel}>
            <Input
              value={circuitBreakerKey}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setCircuitBreakerKey(event.target.value)}
              placeholder={resources.circuitBreakerTrips.keyPlaceholder}
              className="w-56"
            />
          </FilterGroup>

          {hasActiveFilters && (
            <Button
              type="button"
              variant="link"
              onClick={clearFilters}
              className="h-auto p-0 pb-1.5 text-[11.5px] font-medium"
            >
              {resources.circuitBreakerTrips.clearFilters}
            </Button>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-xs text-muted-foreground">{resultsLabel}</span>
        </div>

        {isError && (
          <div className="p-8 text-sm text-status-critical">
            <p>{resources.circuitBreakerTrips.loadError}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(error)}</p>
          </div>
        )}

        {!isError && !isLoading && items.length === 0 && (
          <div className="p-8 text-sm text-muted-foreground">{resources.circuitBreakerTrips.noResults}</div>
        )}

        {!isError && items.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{resources.circuitBreakerTrips.columns.created}</TableHead>
                  <TableHead>{resources.circuitBreakerTrips.columns.key}</TableHead>
                  <TableHead>{resources.circuitBreakerTrips.columns.status}</TableHead>
                  <TableHead>{resources.circuitBreakerTrips.columns.reason}</TableHead>
                  <TableHead>{resources.circuitBreakerTrips.columns.trippedUntil}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-muted-foreground" title={formatUtcTooltip(row.createdAt)}>
                      {formatRunDate(row.createdAt)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.circuitBreakerKey}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(row.status)}>{statusLabel(row.status)}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[320px] truncate text-muted-foreground" title={row.reason}>
                      {row.reason}
                    </TableCell>
                    <TableCell className="text-muted-foreground" title={formatUtcTooltip(row.trippedUntil)}>
                      {formatRunDate(row.trippedUntil)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
